"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Search, Grid, List, Tag } from "lucide-react"
import "../Products/Products.css"
import placeholder from "../../images/placeholder.png"

const AutomobileProducts = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("name")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const API_BASE_URL = "https://imperia-backend.onrender.com/api/products"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="

  const [categories, setCategories] = useState([{ id: "all", name: "All Products" }])

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })

  const generateProductData = (product) => ({
    id: product.productId,
    name: product.name || "Premium Product",
    path: `/products/${product.productId}`,
    image: product.imageUrl || placeholder,
    description:
      product.description ||
      "High-quality product engineered for superior performance and reliability with advanced technology.",
    price: product.price || 0,
    category: product.category || "uncategorized",
  })

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch products by category "Automobile"
        const response = await fetch(`${API_BASE_URL}/category/DA3446`, {
          method: "GET",
          headers: getAuthHeaders(),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        if (result.success && result.data && Array.isArray(result.data)) {
          const productsData = result.data.map(generateProductData)
          setProducts(productsData)
          setFilteredProducts(productsData)

          // Extract unique categories from API data
          const uniqueCategories = [...new Set(productsData.map((product) => product.category).filter(Boolean))]
          const categoryOptions = [
            { id: "all", name: "All Products" },
            ...uniqueCategories.map((cat) => ({
              id: cat,
              name: cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
            })),
          ]
          setCategories(categoryOptions)
        } else {
          throw new Error("Invalid API response format")
        }
      } catch (error) {
        console.error("Error fetching products:", error)
        setError(error.message)
        setProducts([])
        setFilteredProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [])

  useEffect(() => {
    let filtered = products

    if (selectedCategory !== "all") {
      filtered = products.filter((product) => product.category === selectedCategory)
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price
        case "price-high":
          return b.price - a.price
        case "name":
        default:
          return a.name.localeCompare(b.name)
      }
    })

    setFilteredProducts(filtered)
  }, [searchTerm, selectedCategory, products, sortBy])

  const handleProductClick = (path) => {
    navigate(path)
  }

  const ProductCard = ({ product }) => (
    <div
      className={`product-card ${viewMode === "grid" ? "grid-card" : "list-card"}`}
      onClick={() => handleProductClick(product.path)}
    >
      <div className="product-image-container">
        <img src={product.image || "/placeholder.svg"} alt={product.name} className="product-image" />

        {/* Product ID Badge */}
        <div className="product-id-badge">
          <span>{product.id}</span>
        </div>
      </div>
      <div className="product-content">
        {/* Category Badge */}
        <div className="product-category-badge">
          <Tag size={12} />
          <span>{product.category.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
        </div>
        <h3 className="product-title">{product.name}</h3>
        <p className="product-description">{product.description}</p>
        <div className="product-actions">
          <Link to={product.path} className="btn-primary">
            View Details
          </Link>
        </div>
      </div>
    </div>
  )

  return (
    <div className="products-page">
      {/* Products Section */}
      <section className="products-section">
        <div className="products-container">
          <div className="products-header">
            <div className="products-count">Showing {filteredProducts.length} Automobile products</div>
              <div className="view-toggle">
                <button
                  className={`view-button ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                >
                  <Grid size={16} />
                </button>
                <button
                  className={`view-button ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <List size={16} />
                </button>
              </div>
          </div>
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading products...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <h3>Failed to load products</h3>
              <p>Error: {error}</p>
              <button onClick={() => window.location.reload()} className="btn-primary">
                Retry
              </button>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className={`products-grid ${viewMode}-view`}>
              {filteredProducts.map((product) => (
                <ProductCard key={product.productId} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h3>No products found</h3>
              <p>Try adjusting your search criteria or browse all products.</p>
              <button
                onClick={() => {
                  setSearchTerm("")
                  setSelectedCategory("all")
                }}
                className="btn-primary"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default AutomobileProducts
